import { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

type Step = 'role' | 'account' | 'organization';

export default function SignUpPage() {
  const [searchParams] = useSearchParams();
  const preSelectedRole = searchParams.get('role') as 'vendor' | 'client' | null;
  const inviteToken = searchParams.get('invite');

  // If a role was pre-selected via URL, skip the role step
  const [step, setStep] = useState<Step>(
    preSelectedRole === 'vendor' || preSelectedRole === 'client' ? 'account' : 'role'
  );
  const [role, setRole] = useState<'vendor' | 'client'>(
    preSelectedRole === 'vendor' || preSelectedRole === 'client' ? preSelectedRole : 'vendor'
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [localError, setLocalError] = useState('');

  const register = useAuthStore((s) => s.register);
  const isLoading = useAuthStore((s) => s.isLoading);
  const error = useAuthStore((s) => s.error);
  const clearError = useAuthStore((s) => s.clearError);
  const navigate = useNavigate();

  const handleRoleSelect = (selectedRole: 'vendor' | 'client') => {
    setRole(selectedRole);
    setStep('account');
  };

  const handleAccountNext = (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters.');
      return;
    }
    setLocalError('');
    setStep('organization');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError('');
    try {
      await register({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        role,
        organization_name: organizationName,
      });
      navigate(`/check-email?email=${encodeURIComponent(email)}`, { replace: true });
    } catch {
      // Error handled by store
    }
  };

  const displayError = localError || error;
  const steps: Step[] = ['role', 'account', 'organization'];
  const currentStepIndex = steps.indexOf(step);

  return (
    <>
      <div className="mt-2 flex justify-center">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-blue-600 transition-colors">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
          Back to home
        </Link>
      </div>
      <p className="mt-2 text-center text-sm text-slate-500">Create your account</p>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-6 shadow-sm border border-slate-100 rounded-2xl sm:px-10">
          {/* Invite banner */}
          {preSelectedRole && inviteToken && (
            <div className="mb-4 rounded-xl bg-blue-50 border border-blue-200 p-4">
              <p className="text-sm text-blue-700">
                You've been invited to join GuardGrade as a <strong>{preSelectedRole === 'vendor' ? 'Security Vendor' : 'Client'}</strong>. Create your account below.
              </p>
            </div>
          )}

          {displayError && (
            <div role="alert" className="mb-4 rounded-xl bg-red-50 p-4">
              <p className="text-sm text-red-700">{displayError}</p>
            </div>
          )}

          {/* Step indicators */}
          <div className="flex items-center justify-center mb-8 gap-2">
            {steps.map((s, i) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                    step === s
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-sm shadow-blue-500/25'
                      : i < currentStepIndex
                      ? 'bg-blue-50 text-blue-600'
                      : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {i + 1}
                </div>
                {i < 2 && <div className="w-8 h-0.5 bg-slate-200 mx-1" />}
              </div>
            ))}
          </div>

          {/* Step 1: Role Selection */}
          {step === 'role' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500 text-center mb-4">I am a...</p>
              <button
                onClick={() => handleRoleSelect('vendor')}
                className="w-full p-4 border-2 border-slate-200 rounded-xl text-left hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                <p className="font-semibold text-slate-900">Security Vendor</p>
                <p className="text-sm text-slate-500 mt-1">I provide security services and manage guards</p>
              </button>
              <button
                onClick={() => handleRoleSelect('client')}
                className="w-full p-4 border-2 border-slate-200 rounded-xl text-left hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                <p className="font-semibold text-slate-900">Client / Property Manager</p>
                <p className="text-sm text-slate-500 mt-1">I own sites and hire security vendors</p>
              </button>
            </div>
          )}

          {/* Step 2: Account Info */}
          {step === 'account' && (
            <form onSubmit={handleAccountNext} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-slate-700">
                    First name
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="mt-1 block w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-slate-700">
                    Last name
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="mt-1 block w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 block w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700">
                  Confirm password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 block w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-3 pt-2">
                {!preSelectedRole && (
                  <button
                    type="button"
                    onClick={() => setStep('role')}
                    className="flex-1 py-2.5 px-4 border-2 border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                  >
                    Back
                  </button>
                )}
                <button
                  type="submit"
                  className="flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-sm shadow-blue-500/25 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
                >
                  Next
                </button>
              </div>
            </form>
          )}

          {/* Step 3: Organization */}
          {step === 'organization' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="orgName" className="block text-sm font-medium text-slate-700">
                  {role === 'vendor' ? 'Company name' : 'Organization name'}
                </label>
                <input
                  id="orgName"
                  type="text"
                  required
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  className="mt-1 block w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setStep('account')}
                  className="flex-1 py-2.5 px-4 border-2 border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-sm shadow-blue-500/25 disabled:opacity-50 transition-all"
                >
                  {isLoading ? 'Creating account...' : 'Create account'}
                </button>
              </div>
            </form>
          )}

          <p className="mt-6 text-center text-sm text-slate-500">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 hover:text-blue-500 font-semibold">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </>
  );
}
